import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Loading } from '@/components/Shared';

import Login from '@/pages/Login';
import CityLanding from '@/pages/CityLanding';
import SignIn from '@/pages/SignIn';
import AuthCallback from '@/pages/AuthCallback';
import { CustomerLayout } from '@/layouts/CustomerLayout';
import { DriverLayout } from '@/layouts/DriverLayout';
import { AdminLayout } from '@/layouts/AdminLayout';

// Customer pages
import CustomerHome from '@/pages/customer/CustomerHome';
import Catalog from '@/pages/customer/Catalog';
import ProductShop from '@/pages/customer/ProductShop';
import OrderFlow from '@/pages/customer/OrderFlow';
import OrderTrack from '@/pages/customer/OrderTrack';
import CustomerOrders from '@/pages/customer/CustomerOrders';
import CustomerInvoices from '@/pages/customer/CustomerInvoices';
import CustomerProfile from '@/pages/customer/CustomerProfile';

// Driver pages
import DriverToday from '@/pages/driver/DriverToday';
import DriverJob from '@/pages/driver/DriverJob';
import DriverHistory from '@/pages/driver/DriverHistory';
import DriverStats from '@/pages/driver/DriverStats';
import DriverProfile from '@/pages/driver/DriverProfile';

// Admin pages
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminLiveMap from '@/pages/admin/AdminLiveMap';
import AdminOrders from '@/pages/admin/AdminOrders';
import AdminOrderDetail from '@/pages/admin/AdminOrderDetail';
import AdminCustomers from '@/pages/admin/AdminCustomers';
import AdminAddressPins from '@/pages/admin/AdminAddressPins';
import AdminCustomerDetail from '@/pages/admin/AdminCustomerDetail';
import AdminDrivers from '@/pages/admin/AdminDrivers';
import AdminVehicles from '@/pages/admin/AdminVehicles';
import AdminInventory from '@/pages/admin/AdminInventory';
import AdminPricing from '@/pages/admin/AdminPricing';
import AdminBilling from '@/pages/admin/AdminBilling';
import AdminSupport from '@/pages/admin/AdminSupport';
import AdminReports from '@/pages/admin/AdminReports';
import AdminAudit from '@/pages/admin/AdminAudit';
import AdminAI from '@/pages/admin/AdminAI';
import AdminReviews from '@/pages/admin/AdminReviews';
import PumpDashboard from '@/pages/pump/PumpDashboard';
import PumpDailyReport from '@/pages/pump/PumpDailyReport';
import PumpCRM from '@/pages/pump/PumpCRM';
import { PumpLayout } from '@/layouts/PumpLayout';

const HOME_BY_ROLE = { admin: '/admin', driver: '/driver', pump: '/pump' };
const homeFor = (role) => HOME_BY_ROLE[role] || '/app';

const Protected = ({ role, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading label="Starting Mobile Fuel Kart..." />;
  if (!user) return <Navigate to="/signin" replace />;
  if (role && user.role !== role) {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return children;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <Loading label="Starting Mobile Fuel Kart..." />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
};

const C = (P) => <CustomerLayout>{P}</CustomerLayout>;
const D = (P) => <DriverLayout>{P}</DriverLayout>;
const A = (P) => <AdminLayout>{P}</AdminLayout>;
const PU = (P) => <PumpLayout>{P}</PumpLayout>;

function AppRouter() {
  // Read the hash from useLocation() (reactive) — NOT window.location.hash.
  // Detecting session_id synchronously during render (not in useEffect) processes the
  // OAuth callback BEFORE ProtectedRoute/auth checks run, avoiding race conditions.
  const location = useLocation();
  if (location.hash?.includes('session_id=')) { return <AuthCallback />; }
  return (
    <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/diesel-delivery/:city" element={<CityLanding />} />
            <Route path="/signin" element={<SignIn />} />

            {/* Customer */}
            <Route path="/app" element={<Protected role="customer">{C(<CustomerHome />)}</Protected>} />
            <Route path="/app/catalog" element={<Protected role="customer">{C(<Catalog />)}</Protected>} />
            <Route path="/app/shop" element={<Protected role="customer">{C(<ProductShop />)}</Protected>} />
            <Route path="/app/order/:productId" element={<Protected role="customer">{C(<OrderFlow />)}</Protected>} />
            <Route path="/app/track/:orderId" element={<Protected role="customer">{C(<OrderTrack />)}</Protected>} />
            <Route path="/app/orders" element={<Protected role="customer">{C(<CustomerOrders />)}</Protected>} />
            <Route path="/app/invoices" element={<Protected role="customer">{C(<CustomerInvoices />)}</Protected>} />
            <Route path="/app/profile" element={<Protected role="customer">{C(<CustomerProfile />)}</Protected>} />

            {/* Driver */}
            <Route path="/driver" element={<Protected role="driver">{D(<DriverToday />)}</Protected>} />
            <Route path="/driver/job/:orderId" element={<Protected role="driver">{D(<DriverJob />)}</Protected>} />
            <Route path="/driver/history" element={<Protected role="driver">{D(<DriverHistory />)}</Protected>} />
            <Route path="/driver/stats" element={<Protected role="driver">{D(<DriverStats />)}</Protected>} />
            <Route path="/driver/profile" element={<Protected role="driver">{D(<DriverProfile />)}</Protected>} />

            {/* Admin */}
            <Route path="/admin" element={<Protected role="admin">{A(<AdminDashboard />)}</Protected>} />
            <Route path="/admin/ai" element={<Protected role="admin">{A(<AdminAI />)}</Protected>} />
            <Route path="/admin/reviews" element={<Protected role="admin">{A(<AdminReviews />)}</Protected>} />
            <Route path="/admin/pump" element={<Protected role="admin">{A(<PumpDashboard />)}</Protected>} />
            <Route path="/admin/pump/report/:date" element={<Protected role="admin">{A(<PumpDailyReport />)}</Protected>} />
            <Route path="/admin/pump/crm" element={<Protected role="admin">{A(<PumpCRM />)}</Protected>} />
            <Route path="/pump" element={<Protected role="pump">{PU(<PumpDashboard />)}</Protected>} />
            <Route path="/pump/report/:date" element={<Protected role="pump">{PU(<PumpDailyReport />)}</Protected>} />
            <Route path="/pump/crm" element={<Protected role="pump">{PU(<PumpCRM />)}</Protected>} />
            <Route path="/admin/live-map" element={<Protected role="admin">{A(<AdminLiveMap />)}</Protected>} />
            <Route path="/admin/orders" element={<Protected role="admin">{A(<AdminOrders />)}</Protected>} />
            <Route path="/admin/orders/:orderId" element={<Protected role="admin">{A(<AdminOrderDetail />)}</Protected>} />
            <Route path="/admin/customers" element={<Protected role="admin">{A(<AdminCustomers />)}</Protected>} />
            <Route path="/admin/address-pins" element={<Protected role="admin">{A(<AdminAddressPins />)}</Protected>} />
            <Route path="/admin/customers/:id" element={<Protected role="admin">{A(<AdminCustomerDetail />)}</Protected>} />
            <Route path="/admin/drivers" element={<Protected role="admin">{A(<AdminDrivers />)}</Protected>} />
            <Route path="/admin/vehicles" element={<Protected role="admin">{A(<AdminVehicles />)}</Protected>} />
            <Route path="/admin/inventory" element={<Protected role="admin">{A(<AdminInventory />)}</Protected>} />
            <Route path="/admin/pricing" element={<Protected role="admin">{A(<AdminPricing />)}</Protected>} />
            <Route path="/admin/billing" element={<Protected role="admin">{A(<AdminBilling />)}</Protected>} />
            <Route path="/admin/support" element={<Protected role="admin">{A(<AdminSupport />)}</Protected>} />
            <Route path="/admin/reports" element={<Protected role="admin">{A(<AdminReports />)}</Protected>} />
            <Route path="/admin/audit" element={<Protected role="admin">{A(<AdminAudit />)}</Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
